/**
 * Standards Selection Step Component
 *
 * Allows clients to select which standards from .equilateral-standards/ they want to follow.
 * Standards selection is based on their technology stack and compliance requirements.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect } from 'react';
import { Card, Label, Checkbox, Badge, Alert, Button, Spinner } from 'flowbite-react';
import {
  HiCheckCircle,
  HiInformationCircle,
  HiFolder,
  HiLightningBolt
} from 'react-icons/hi';
import type { StackSelection } from './StackSelectionStep';

export interface StandardsSelection {
  selectedCategories: string[];
  customStandards?: string;
}

interface StandardsSelectionStepProps {
  stackSelection: StackSelection;
  standardsSelection: StandardsSelection;
  onChange: (standardsSelection: StandardsSelection) => void;
}

interface StandardCategory {
  id: string;
  name: string;
  description: string;
  recommended: boolean;
  tags: string[];
}

// Standard categories from .equilateral-standards/
const STANDARD_CATEGORIES: StandardCategory[] = [
  {
    id: 'serverless-saas-aws',
    name: 'Serverless SaaS AWS',
    description: 'AWS Lambda, API Gateway, SAM patterns, cost optimization',
    recommended: false,
    tags: ['aws', 'serverless', 'lambda', 'api-gateway', 'backend']
  },
  {
    id: 'frontend-development',
    name: 'Frontend Development',
    description: 'React, TypeScript, UI/UX patterns, component architecture',
    recommended: false,
    tags: ['react', 'typescript', 'frontend', 'ui']
  },
  {
    id: 'backend',
    name: 'Backend Development',
    description: 'Backend architecture, API design, data processing patterns',
    recommended: false,
    tags: ['backend', 'api', 'architecture']
  },
  {
    id: 'multi-agent-orchestration',
    name: 'Multi-Agent Orchestration',
    description: 'Agent coordination, workflows, event-driven architecture',
    recommended: false,
    tags: ['agents', 'orchestration', 'workflows', 'event-driven']
  },
  {
    id: 'compliance-security',
    name: 'Compliance & Security',
    description: 'Security best practices, GDPR, CCPA, SOC2, HIPAA compliance',
    recommended: true,
    tags: ['security', 'compliance', 'gdpr', 'ccpa', 'hipaa', 'soc2']
  },
  {
    id: 'cost-optimization',
    name: 'Cost Optimization',
    description: 'Cost management, resource optimization, AWS cost analysis',
    recommended: true,
    tags: ['cost', 'optimization', 'aws', 'budget']
  },
  {
    id: 'database',
    name: 'Database Patterns',
    description: 'PostgreSQL, data modeling, query optimization, migrations',
    recommended: false,
    tags: ['database', 'postgresql', 'sql', 'data-modeling']
  },
  {
    id: 'testing',
    name: 'Testing Standards',
    description: 'Testing strategies, patterns, unit tests, integration tests',
    recommended: true,
    tags: ['testing', 'qa', 'quality']
  },
  {
    id: 'deployment',
    name: 'Deployment',
    description: 'CI/CD, deployment strategies, infrastructure as code',
    recommended: false,
    tags: ['deployment', 'cicd', 'infrastructure']
  },
  {
    id: 'real-time-systems',
    name: 'Real-Time Systems',
    description: 'WebSocket, real-time communication, event streaming',
    recommended: false,
    tags: ['websocket', 'real-time', 'streaming']
  }
];

export const StandardsSelectionStep: React.FC<StandardsSelectionStepProps> = ({
  stackSelection,
  standardsSelection,
  onChange
}) => {
  const [recommendedCategories, setRecommendedCategories] = useState<string[]>([]);

  // Calculate recommended standards based on stack selection
  useEffect(() => {
    const recommended: string[] = [];

    // Always recommend security and cost optimization
    recommended.push('compliance-security', 'cost-optimization', 'testing');

    // Frontend recommendations
    if (stackSelection.frontendStack.some(s => s.includes('react') || s.includes('vue') || s.includes('angular'))) {
      recommended.push('frontend-development');
    }

    // Backend recommendations
    if (stackSelection.backendStack.length > 0) {
      recommended.push('backend');
    }

    // AWS serverless recommendations
    if (
      stackSelection.cloudProvider.includes('aws') &&
      (stackSelection.backendStack.includes('node-serverless') || stackSelection.architecture.includes('serverless'))
    ) {
      recommended.push('serverless-saas-aws');
    }

    // Database recommendations
    if (stackSelection.database.length > 0) {
      recommended.push('database');
    }

    // Architecture recommendations
    if (stackSelection.architecture.includes('event-driven')) {
      recommended.push('multi-agent-orchestration');
    }

    if (stackSelection.architecture.includes('microservices')) {
      recommended.push('deployment');
    }

    // Real-time recommendations
    if (stackSelection.additionalTechnologies?.toLowerCase().includes('websocket') ||
        stackSelection.additionalTechnologies?.toLowerCase().includes('real-time')) {
      recommended.push('real-time-systems');
    }

    setRecommendedCategories([...new Set(recommended)]);
  }, [stackSelection]);

  const toggleCategory = (categoryId: string) => {
    const newSelection = standardsSelection.selectedCategories.includes(categoryId)
      ? standardsSelection.selectedCategories.filter(id => id !== categoryId)
      : [...standardsSelection.selectedCategories, categoryId];

    onChange({
      ...standardsSelection,
      selectedCategories: newSelection
    });
  };

  const selectAllRecommended = () => {
    onChange({
      ...standardsSelection,
      selectedCategories: [...new Set([...standardsSelection.selectedCategories, ...recommendedCategories])]
    });
  };

  const selectAll = () => {
    onChange({
      ...standardsSelection,
      selectedCategories: STANDARD_CATEGORIES.map(cat => cat.id)
    });
  };

  const selectNone = () => {
    onChange({
      ...standardsSelection,
      selectedCategories: []
    });
  };

  const handleCustomStandardsChange = (value: string) => {
    onChange({
      ...standardsSelection,
      customStandards: value
    });
  };

  const isRecommended = (categoryId: string) => recommendedCategories.includes(categoryId);
  const selectedCount = standardsSelection.selectedCategories.length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Standards Selection</h2>
        <p className="text-gray-600 mt-1">
          Choose which standards you want to follow. We've recommended standards based on your
          technology stack.
        </p>
      </div>

      {recommendedCategories.length > 0 && (
        <Alert color="info" icon={HiLightningBolt}>
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium">
                {recommendedCategories.length} standards recommended
              </span>{' '}
              based on your technology stack
            </div>
            <Button
              size="xs"
              color="info"
              onClick={selectAllRecommended}
            >
              Select Recommended
            </Button>
          </div>
        </Alert>
      )}

      {/* Quick Actions */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {selectedCount} of {STANDARD_CATEGORIES.length} standards selected
        </div>
        <div className="flex space-x-2">
          <Button size="xs" color="gray" onClick={selectAll}>
            Select All
          </Button>
          <Button size="xs" color="gray" onClick={selectNone}>
            Select None
          </Button>
        </div>
      </div>

      {/* Standards Categories */}
      <div className="space-y-3">
        {STANDARD_CATEGORIES.map((category) => {
          const isSelected = standardsSelection.selectedCategories.includes(category.id);
          const recommended = isRecommended(category.id);

          return (
            <Card
              key={category.id}
              className={`cursor-pointer transition-all ${
                isSelected ? 'border-2 border-indigo-600 bg-indigo-50' : 'hover:bg-gray-50'
              }`}
              onClick={() => toggleCategory(category.id)}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleCategory(category.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <HiFolder className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                    <h3 className="font-semibold text-gray-900">{category.name}</h3>
                    {recommended && (
                      <Badge color="success" icon={HiLightningBolt} size="sm">
                        Recommended
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {category.tags.map((tag) => (
                      <Badge key={tag} color="gray" size="xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
                {isSelected && (
                  <div className="flex-shrink-0">
                    <HiCheckCircle className="w-6 h-6 text-indigo-600" />
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Custom Standards */}
      <div className="space-y-2">
        <Label htmlFor="customStandards">
          Custom Standards or Notes (Optional)
        </Label>
        <textarea
          id="customStandards"
          value={standardsSelection.customStandards || ''}
          onChange={(e) => handleCustomStandardsChange(e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
          placeholder="List any custom standards, internal guidelines, or specific requirements you want to follow..."
        />
      </div>

      {/* Information Box */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <HiInformationCircle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-2">How standards selection helps:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Document Templates:</strong> Pre-populated with relevant standards content
              </li>
              <li>
                <strong>Compliance Checks:</strong> Automatic validation against selected standards
              </li>
              <li>
                <strong>Best Practices:</strong> Recommendations tailored to your selections
              </li>
              <li>
                <strong>Team Guidance:</strong> Standards available for reference in the platform
              </li>
            </ul>
            <p className="mt-2 text-gray-600">
              You can modify these selections later from your organization settings.
            </p>
          </div>
        </div>
      </div>

      {selectedCount === 0 && (
        <Alert color="warning" icon={HiInformationCircle}>
          <span className="font-medium">No standards selected.</span> While optional, selecting
          standards helps us provide better guidance and documentation templates.
        </Alert>
      )}
    </div>
  );
};

export default StandardsSelectionStep;
